// ============================================================================
// DynamicListTable — runtime отображение динамического списка
// ============================================================================

import { useState, useEffect } from 'react';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { formatValue } from '@/lib/format';

const log = createLogger('DynamicListTable');

interface DynamicListColumn {
  field: string;
  synonym: string;
  width?: number;
  visible?: boolean;
  align?: 'left' | 'center' | 'right';
  format?: string;
}

interface DynamicListTableProps {
  baseTable?: string;
  query?: string;
  columns?: DynamicListColumn[];
  parameters?: Record<string, any>;
  onRowClick?: (row: Record<string, any>) => void;
}

export function DynamicListTable({ baseTable, query, columns, parameters, onRowClick }: DynamicListTableProps) {
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [derivedColumns, setDerivedColumns] = useState<DynamicListColumn[]>([]);
  const pageSize = 50;

  useEffect(() => {
    loadData();
  }, [baseTable, query, sortField, sortDir, page]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const sdk = getSDK();

      if (baseTable) {
        // base_table mode — simple list
        const result = await sdk.metadata.listTableData({
          schema: 'public',
          table: baseTable,
          pageSize,
          page,
          orderBy: sortField || undefined,
          orderDesc: sortDir === 'desc',
        });
        const flat = (result.rows || []).map((r: any) => {
          const row: Record<string, any> = { id: r.id };
          for (const v of r.values || []) {
            row[v.column] = v.value;
          }
          return row;
        });
        setRows(flat);
        if (flat.length > 0) {
          // Колонки по умолчанию — из первой строки (без системных)
          const keys = Object.keys(flat[0]).filter((k) =>
            !['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'is_marked', 'is_posted'].includes(k) &&
            !k.endsWith('_presentation'));
          setDerivedColumns(keys.map((k) => ({ field: k, synonym: k, visible: true })));
        }
      } else if (query) {
        // query mode — произвольный hydraql запрос (колонки могут быть любыми)
        const result = await sdk.metadata.executeHydraql({
          query,
          params: (parameters || {}) as any,
        });
        const flat = (result.rows || []).map((row: any) => {
          const obj: Record<string, any> = {};
          for (const v of row?.values || []) {
            obj[v.column] = v.value === '<nil>' ? null : v.value;
          }
          return obj;
        });
        setRows(flat);
        // Колонки по умолчанию — из результата запроса (произвольные!)
        const cols = (result.columns || []).filter((c: string) => !c.endsWith('_presentation'));
        setDerivedColumns(cols.map((c) => ({ field: c, synonym: c, visible: true })));
      }
    } catch (e: any) {
      log.error('Failed to load dynamic list', { error: e });
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const visibleCols = (columns && columns.length > 0 ? columns : derivedColumns).filter(c => c.visible !== false);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-4"><div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  if (error) {
    return <div className="p-4 text-sm text-destructive">{error}</div>;
  }

  return (
    <div className="overflow-auto border rounded-lg">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 sticky top-0">
          <tr>
            {visibleCols.map(col => (
              <th
                key={col.field}
                className="text-left px-2 py-1.5 font-medium cursor-pointer hover:bg-muted/70 select-none"
                style={col.width ? { width: col.width } : undefined}
                onClick={() => handleSort(col.field)}
              >
                {col.synonym || col.field}
                {sortField === col.field && (sortDir === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={visibleCols.length} className="p-4 text-center text-muted-foreground">Нет данных</td></tr>
          ) : (
            rows.map((row, idx) => (
              <tr
                key={row.id || idx}
                className={`hover:bg-muted/30 cursor-pointer ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                onClick={() => onRowClick?.(row)}
              >
                {visibleCols.map(col => (
                  <td key={col.field} className="px-2 py-1 border-t border-muted/30" style={col.align === 'right' ? { textAlign: 'right' } : col.align === 'center' ? { textAlign: 'center' } : undefined}>
                    {row[col.field] != null ? formatValue(row[col.field], col.format) : ''}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {/* Pagination */}
      <div className="flex items-center justify-between px-2 py-1 text-[10px] text-muted-foreground border-t">
        <span>Стр. {page + 1} ({rows.length} записей)</span>
        <div className="flex gap-1">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2 py-0.5 border rounded hover:bg-muted disabled:opacity-50">◀</button>
          <button onClick={() => setPage(p => p + 1)} disabled={rows.length < pageSize} className="px-2 py-0.5 border rounded hover:bg-muted disabled:opacity-50">▶</button>
        </div>
      </div>
    </div>
  );
}
